import React, { useState, useEffect } from 'react';
import { Search, BookOpen, Plus, X, Loader2, Edit3, Trash2, Paperclip, ExternalLink } from 'lucide-react';

export default function ModelPapersManagement() {
  const [dataList, setDataList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableLoading, setTableLoading] = useState(false);

  // Dynamic Course Cascades & Term Options
  const [courseCascades, setCourseCascades] = useState([]);
  const [availableTerms, setAvailableTerms] = useState([]);

  // Modal Controls
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);

  // Pagination Parameters
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Form State
  const [formData, setFormData] = useState({
    target_course: '',
    current_semester: '',
    academic_year: '2026-2027',
    reference_link: '',
    file_attachment_url: ''
  });

  useEffect(() => {
    fetchModelPapers();
  }, [searchTerm]);

  useEffect(() => {
    fetchCourseCascades();
  }, []);

  const fetchModelPapers = async () => {
    setTableLoading(true);
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/model-papers?search=${encodeURIComponent(searchTerm)}`);
      const result = await res.json();
      if (result.success) setDataList(result.data || []);
      else setDataList([]);
    } catch (err) {
      console.error(err);
      setDataList([]);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchCourseCascades = async () => {
    try {
      const res = await fetch('https://metapi.emedhatechnologies.com/api/model-papers/course-cascades');
      const result = await res.json();
      if (result.success && result.data.length > 0) {
        setCourseCascades(result.data);
      }
    } catch (err) {
      console.error("Failed to load course options:", err);
    }
  };

  // 🔄 Dynamic term generator based on Course selection
  const handleCourseSelectionChange = (courseName) => {
    const selected = courseCascades.find(c => c.course_name.trim() === courseName.trim());
    let terms = [];

    if (selected) {
      if (selected.semester_details && selected.semester_details.length > 0) {
        terms = selected.semester_details.map(s => s.term_label);
      } else if (selected.syllabus_structure && selected.syllabus_structure.length > 0) {
        terms = selected.syllabus_structure.map(s => s.term);
      } else {
        const total = selected.structure_type === 'Semester' ? (selected.minimum_duration || 3) * 2 : (selected.minimum_duration || 1);
        for (let i = 1; i <= total; i++) {
          terms.push(selected.structure_type === 'Semester' ? `Semester ${i}` : `Year ${i}`);
        }
      }
    }

    setAvailableTerms(terms);
    setFormData(prev => ({
      ...prev,
      target_course: courseName,
      current_semester: terms[0] || ''
    }));
  };

  const handleOpenAddModal = () => {
    setEditingId(null);
    setUploadFile(null);

    const firstCourse = courseCascades[0]?.course_name.trim() || '';
    handleCourseSelectionChange(firstCourse);

    setFormData({
      target_course: firstCourse,
      current_semester: '',
      academic_year: '2026-2027',
      reference_link: '',
      file_attachment_url: ''
    });

    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingId(item.id);
    setUploadFile(null);

    const selected = courseCascades.find(c => c.course_name.trim() === item.target_course.trim());
    let terms = [];
    if (selected) {
      if (selected.semester_details && selected.semester_details.length > 0) {
        terms = selected.semester_details.map(s => s.term_label);
      } else if (selected.syllabus_structure && selected.syllabus_structure.length > 0) {
        terms = selected.syllabus_structure.map(s => s.term);
      }
    }
    setAvailableTerms(terms);

    setFormData({
      target_course: item.target_course || '',
      current_semester: item.current_semester || '',
      academic_year: item.academic_year || '2026-2027',
      reference_link: item.reference_link || '',
      file_attachment_url: item.file_attachment_url || ''
    });

    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const payloadFormData = new FormData();
    payloadFormData.append('payload', JSON.stringify(formData));
    if (uploadFile) {
      payloadFormData.append('file_attachment', uploadFile);
    }

    const endpoint = editingId ? `update-entry/${editingId}` : 'add-entry';
    const method = editingId ? 'PUT' : 'POST';

    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/model-papers/${endpoint}`, {
        method: method,
        body: payloadFormData
      });
      const result = await res.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchModelPapers();
      } else {
        alert(result.message || "Operation failed.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to remove this model paper?")) return;
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/model-papers/delete-entry/${id}`, {
        method: 'PUT'
      });
      if ((await res.json()).success) fetchModelPapers();
    } catch (err) {
      console.error(err);
    }
  };

  // Pagination Window Range Computation
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = dataList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(dataList.length / itemsPerPage);

  return (
    <div style={styles.container}>
      
      {/* TOP HEADER BAR */}
      <div style={styles.topActionBar}>
        <div>
          <h2 style={styles.mainTitle}>Course Model Question Papers</h2>
          <p style={styles.mainSubtitle}>Publish sample question papers, model test sets, and previous exam question banks</p>
        </div>
        <button onClick={handleOpenAddModal} style={styles.addBtn}>
          <Plus size={15} style={{ marginRight: '6px' }} /> Add New Model Paper
        </button>
      </div>

      {/* SEARCH AND RECORD COUNT STRIP */}
      <div style={styles.filterControlPanel}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by course, term, or academic year..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            style={styles.tableSearchInput}
          />
        </div>
        <div style={styles.totalIndicator}>Active Model Papers: <strong style={{ color: '#1c6ec9' }}>{dataList.length}</strong></div>
      </div>

      {/* MASTER DATA TABLE */}
      {tableLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#1c6ec9" />
        </div>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={styles.dataTable}>
            <thead>
              <tr>
                <th style={styles.tableTh}>Course Track</th>
                <th style={styles.tableTh}>Term / Semester</th>
                <th style={styles.tableTh}>Academic Year</th>
                <th style={styles.tableTh}>Model Paper Attachment</th>
                <th style={styles.tableTh}>Reference URL</th>
                <th style={styles.tableTh}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dataList.length === 0 ? (
                <tr><td colSpan="6" style={styles.noDataTd}>No model papers uploaded yet.</td></tr>
              ) : (
                currentItems.map((item) => (
                  <tr key={item.id} style={styles.tableTr}>
                    <td style={{ ...styles.tableTd, fontWeight: '700', color: '#1c6ec9' }}>{item.target_course}</td>
                    <td style={styles.tableTd}><span style={styles.termLabel}>{item.current_semester}</span></td>
                    <td style={{ ...styles.tableTd, fontWeight: '600' }}>{item.academic_year}</td>
                    <td style={styles.tableTd}>
                      {item.file_attachment_url ? (
                        <a href={item.file_attachment_url} target="_blank" rel="noreferrer" style={styles.fileAnchor}>
                          <Paperclip size={12} style={{ marginRight: '4px' }} /> View Paper
                        </a>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>No File</span>
                      )}
                    </td>
                    <td style={styles.tableTd}>
                      {item.reference_link ? (
                        <a href={item.reference_link} target="_blank" rel="noreferrer" style={styles.linkAnchor}>
                          <ExternalLink size={12} style={{ marginRight: '4px' }} /> Open Link
                        </a>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>None</span>
                      )}
                    </td>
                    <td style={styles.tableTd}>
                      <div style={styles.actionBtnGroup}>
                        <button onClick={() => handleOpenEditModal(item)} style={styles.editRowBtn} title="Edit"><Edit3 size={12} /></button>
                        <button onClick={() => handleDeleteEntry(item.id)} style={styles.deleteRowBtn} title="Delete"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* PAGINATION TOOLBAR */}
      {dataList.length > 0 && (
        <div style={styles.paginationDock}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            style={currentPage === 1 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Previous
          </button>
          <span style={styles.pageIndicator}>
            Page <strong>{currentPage}</strong> of {totalPages || 1}
          </span>
          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            style={currentPage === totalPages || totalPages === 0 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Next
          </button>
        </div>
      )}

      {/* POPUP MANAGEMENT MODAL */}
      {isModalOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalWrapperCard}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>
                {editingId ? 'Modify Model Question Paper' : 'Upload New Model Question Paper'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} style={styles.modalCloseX}><X size={18} /></button>
            </div>

            <form onSubmit={handleFormSubmit} style={styles.modalForm}>
              
              {/* 1. Course Stream Selection */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Select Course Stream *</label>
                <select
                  required
                  value={formData.target_course}
                  onChange={(e) => handleCourseSelectionChange(e.target.value)}
                  style={styles.formSelect}
                >
                  <option value="">-- Choose Course Stream --</option>
                  {courseCascades.map((course) => (
                    <option key={course.id} value={course.course_name.trim()}>
                      {course.course_code} - {course.course_name.trim()} ({course.structure_type})
                    </option>
                  ))}
                </select>
              </div>

              {/* 2. Term / Semester and 3. Academic Year */}
              <div style={styles.formSplitGrid}>
                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel}>Term / Semester *</label>
                  <select
                    required
                    value={formData.current_semester}
                    onChange={(e) => setFormData({ ...formData, current_semester: e.target.value })}
                    style={styles.formSelect}
                  >
                    <option value="">-- Select Term --</option>
                    {availableTerms.map((term, idx) => (
                      <option key={idx} value={term}>{term}</option>
                    ))}
                  </select>
                </div>

                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel}>Academic Year *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. 2026-2027"
                    value={formData.academic_year}
                    onChange={(e) => setFormData({ ...formData, academic_year: e.target.value })}
                    style={styles.formInput}
                  />
                </div>
              </div>

              {/* 4. Document File Upload */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Upload Model Paper (PDF, Excel, Images, Word - Optional)</label>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.docx,.doc"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  style={styles.formFileInput}
                />
              </div>

              {/* 5. Online Reference Link */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Reference URL / Drive Link (Optional)</label>
                <input
                  type="url"
                  placeholder="https://drive.google.com/..."
                  value={formData.reference_link}
                  onChange={(e) => setFormData({ ...formData, reference_link: e.target.value })}
                  style={styles.formInput}
                />
              </div>

              <div style={styles.formActionDock}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={styles.formCancelBtn}>Cancel</button>
                <button type="submit" style={styles.formSaveBtn}>Save Model Paper</button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}

// FORMATTING STYLES OBJECT MATRIX
const styles = {
  container: { width: '100%', boxSizing: 'border-box' },
  topActionBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' },
  mainTitle: { margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' },
  mainSubtitle: { margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' },
  addBtn: { background: 'linear-gradient(90deg, #e30504 0%, #a80302 100%)', border: 'none', borderRadius: '12px', color: '#fff', padding: '12px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' },
  filterControlPanel: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', borderBottom: '1px solid #edf2f7', paddingBottom: '14px' },
  searchWrapper: { position: 'relative', display: 'flex', alignItems: 'center', flex: 1, maxWidth: '340px', width: '100%' },
  searchIcon: { position: 'absolute', left: '12px', color: '#94a3b8' },
  tableSearchInput: { width: '100%', padding: '9px 12px 9px 36px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '9px', color: '#0f172a', fontSize: '13.5px', outline: 'none' },
  totalIndicator: { fontSize: '14.5px', fontWeight: '600', color: '#334155' },
  dataTable: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  tableTh: { padding: '12px 14px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', whiteSpace: 'nowrap' },
  tableTr: { borderBottom: '1px solid #f1f5f9', background: '#ffffff' },
  tableTd: { padding: '14px 14px', color: '#475569', fontSize: '13.5px', verticalAlign: 'middle', whiteSpace: 'nowrap' },
  noDataTd: { padding: '36px 14px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', fontWeight: '500' },
  termLabel: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '3px 8px', borderRadius: '5px', fontSize: '11.5px', fontWeight: '600' },
  fileAnchor: { display: 'inline-flex', alignItems: 'center', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1c6ec9', padding: '4px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', textDecoration: 'none' },
  linkAnchor: { display: 'inline-flex', alignItems: 'center', background: '#f8fafc', border: '1px solid #cbd5e1', color: '#334155', padding: '4px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', textDecoration: 'none' },
  actionBtnGroup: { display: 'flex', gap: '6px' },
  editRowBtn: { background: '#ffffff', border: '1px solid #cbd5e1', color: '#475569', padding: '6px', borderRadius: '6px', cursor: 'pointer' },
  deleteRowBtn: { background: '#ffffff', border: '1px solid #fecaca', color: '#b91c1c', padding: '6px', borderRadius: '6px', cursor: 'pointer' },
  modalBackdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.25)', backdropFilter: 'blur(8px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modalWrapperCard: { background: '#ffffff', border: '1px solid rgba(255,255,255,0.8)', borderRadius: '20px', width: '100%', maxWidth: '540px', maxHeight: '85vh', padding: '24px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' },
  modalCloseX: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' },
  modalForm: { display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '14px', overflowY: 'auto' },
  formSplitGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  fieldContainer: { display: 'flex', flexDirection: 'column', gap: '4px' },
  fieldLabel: { color: '#64748b', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  formInput: { width: '100%', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#0f172a', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  formSelect: { padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#0f172a', fontSize: '13px', outline: 'none' },
  formFileInput: { fontSize: '12px', color: '#475569', padding: '6px 0' },
  formActionDock: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' },
  formCancelBtn: { background: 'transparent', border: '1px solid #e2e8f0', color: '#64748b', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer' },
  paginationDock: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #edf2f7' },
  pageIndicator: { fontSize: '13px', color: '#475569' },
  pageBtn: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 14px', color: '#334155', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' },
  pageBtnDisabled: { background: '#f8fafc', color: '#94a3b8', cursor: 'not-allowed', borderColor: '#e2e8f0' },
  formSaveBtn: { background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)', border: 'none', color: '#fff', padding: '9px 16px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }
};